import {
  AssistantChatTransport,
  type AssistantChatResumableOptions,
} from '@assistant-ui/react-ai-sdk';
import { HttpChatTransportInitOptions, UIMessage, UIMessageChunk } from 'ai';

import { CHAT_ANALYTICS_EVENTS } from '@/lib/constants/post-message-types';

import {
  applyChunkToStepTimings,
  createStepTimingState,
  finalizeStepTimings,
  isStepTimingChunk,
} from './step-timing-reducer';
import { setLiveFinished, setLiveRequestStart } from './stores/live-trail-timings';

const DEFAULT_FIRST_CHUNK_TIMEOUT_MS = 45000;

export class FirstChunkTimeoutError extends Error {
  constructor(timeoutMs: number) {
    super(`Timeout waiting for first response chunk after ${timeoutMs}ms`);
    this.name = 'FirstChunkTimeoutError';
  }
}

interface AnalyticsChatTransportOptions extends HttpChatTransportInitOptions<UIMessage> {
  resumable?: AssistantChatResumableOptions;
  /** Send the identity cookie (`credentials: 'include'`). Server-persistence tenants only. */
  credentialed?: boolean;
  onStreamStart?: (event: string, properties: Record<string, unknown>) => void;
  onStreamingStarted?: (threadId: string) => void;
  onStreamFinish?: (event: {
    threadId: string;
    durationMs: number;
    stepTimings: import('./types').StepTimings;
  }) => void;
  firstChunkTimeoutMs?: number;
}

/**
 * Chat POSTs and resumable-reconnect GETs must carry the tenant-API identity cookie
 * (`credentials: 'include'`): the chat POST mints it and the server-persistence/resume reads are
 * authorized by it. Applied ONLY for server-persistence tenants (the `credentialed` flag) — other
 * tenants keep the default uncredentialed fetch, so their requests stay byte-identical and never
 * send cookies. Scoped here (chat-API traffic only) rather than in fetchWithTimeout, which also
 * serves `Access-Control-Allow-Origin: *` endpoints where credentialed requests would be blocked.
 */
function withCredentials(baseFetch: typeof fetch | undefined): typeof fetch {
  const resolvedFetch: typeof fetch = baseFetch ?? ((...args) => fetch(...args));
  return (input, init) => resolvedFetch(input, { ...init, credentials: 'include' });
}

export class AnalyticsChatTransport extends AssistantChatTransport<UIMessage> {
  private onStreamStart?: AnalyticsChatTransportOptions['onStreamStart'];
  private onStreamingStarted?: AnalyticsChatTransportOptions['onStreamingStarted'];
  private onStreamFinish?: AnalyticsChatTransportOptions['onStreamFinish'];
  private firstChunkTimeoutMs: number;
  private currentThreadId: string | null = null;
  private requestStartTime: number | null = null;

  constructor({
    onStreamStart,
    onStreamingStarted,
    onStreamFinish,
    firstChunkTimeoutMs = DEFAULT_FIRST_CHUNK_TIMEOUT_MS,
    credentialed = false,
    ...options
  }: AnalyticsChatTransportOptions) {
    super({ ...options, fetch: credentialed ? withCredentials(options.fetch) : options.fetch });
    this.onStreamStart = onStreamStart;
    this.onStreamingStarted = onStreamingStarted;
    this.onStreamFinish = onStreamFinish;
    this.firstChunkTimeoutMs = firstChunkTimeoutMs;
  }

  setRequestContext(threadId: string | null, startTime: number) {
    this.currentThreadId = threadId;
    this.requestStartTime = startTime;
    if (threadId) {
      // Seed the live mirror so the trail can render the pre-first-chunk gap.
      setLiveRequestStart({ threadId, requestStartedAt: startTime });
    }
  }

  protected processResponseStream(
    stream: ReadableStream<Uint8Array>,
  ): ReadableStream<UIMessageChunk> {
    const baseStream = super.processResponseStream(stream);

    const {
      onStreamStart,
      onStreamingStarted,
      onStreamFinish,
      currentThreadId,
      requestStartTime,
      firstChunkTimeoutMs,
    } = this;

    const hasTimeout = firstChunkTimeoutMs > 0;
    const hasCallbacks = !!(onStreamStart || onStreamingStarted || onStreamFinish);

    if (!hasTimeout && !hasCallbacks) {
      return baseStream;
    }

    let startStepDetected = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const timingState = createStepTimingState(requestStartTime ?? undefined);

    let finalized = false;
    const finalize = (): void => {
      if (finalized) return;
      finalized = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      if (!currentThreadId || !requestStartTime || !onStreamFinish) return;
      const finishAt = Date.now();
      const stepTimings = finalizeStepTimings({
        state: timingState,
        finishAt,
        threadId: currentThreadId,
      });
      setLiveFinished({ threadId: currentThreadId, finishedAt: finishAt });
      onStreamFinish({
        threadId: currentThreadId,
        durationMs: finishAt - requestStartTime,
        stepTimings,
      });
    };

    const transformed = baseStream.pipeThrough(
      new TransformStream<UIMessageChunk, UIMessageChunk>({
        start(controller) {
          if (firstChunkTimeoutMs > 0) {
            timeoutId = setTimeout(() => {
              if (!startStepDetected) {
                controller.error(new FirstChunkTimeoutError(firstChunkTimeoutMs));
              }
            }, firstChunkTimeoutMs);
          }
        },
        transform(chunk, controller) {
          if (!startStepDetected && chunk.type === 'start-step') {
            startStepDetected = true;

            if (timeoutId) {
              clearTimeout(timeoutId);
              timeoutId = null;
            }

            if (currentThreadId && requestStartTime && onStreamStart) {
              onStreamingStarted?.(currentThreadId);
              onStreamStart(CHAT_ANALYTICS_EVENTS.RESPONSE_STREAM_STARTED, {
                threadId: currentThreadId,
                timeToFirstChunkMs: Date.now() - requestStartTime,
                isDocumentHidden:
                  typeof document !== 'undefined' ? document.visibilityState === 'hidden' : false,
              });
            }
          }

          if (isStepTimingChunk(chunk)) {
            applyChunkToStepTimings({
              chunk,
              now: Date.now(),
              state: timingState,
              threadId: currentThreadId,
              trackGapBefore: true,
            });
          }

          controller.enqueue(chunk);
        },
      }),
    );

    // Wrap so finalize() runs on completion, error, and cancel — flush() only
    // fires on normal close.
    const reader = transformed.getReader();
    return new ReadableStream<UIMessageChunk>({
      async pull(controller) {
        try {
          const { value, done } = await reader.read();
          if (done) {
            finalize();
            controller.close();
            return;
          }
          controller.enqueue(value);
        } catch (error) {
          finalize();
          controller.error(error);
        }
      },
      cancel(reason) {
        finalize();
        return reader.cancel(reason);
      },
    });
  }
}
